import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import NavBar from './NavBar';
import Footer from './Footer';
import { getCart, syncCart, getProducts } from '../api';
import { Eye, EyeOff } from 'lucide-react';


const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout } = useAuth();
  
  // Deriving activePage from the URL path
  const activePage = location.pathname.split('/').pop() || 'home';

  const [modal, setModal] = useState({ show: false, title: '', message: '', onConfirm: null, type: 'confirm' });
  const [modalInput, setModalInput] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [cartItems, setCartItems] = useState(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try { return JSON.parse(savedCart); } catch (e) { console.error('Failed to parse cart'); }
    }
    return [];
  });
  const [checkoutItems, setCheckoutItems] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Cart sync effects
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
    if (currentUser && currentUser.id) {
      const timer = setTimeout(async () => {
        try { await syncCart(currentUser.id, cartItems); } catch (err) { console.error('Failed to sync cart:', err); }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [cartItems, currentUser]);

  useEffect(() => {
    const loadBackendCart = async () => {
      if (currentUser && currentUser.id) {
        try {
          const backendCart = await getCart(currentUser.id);
          const items = backendCart?.data?.items || backendCart?.items;
          if (items && Array.isArray(items) && items.length > 0) {
            setCartItems(items);
          } else {
            // If backend has no items, sync current localStorage cart to backend
            const localCart = JSON.parse(localStorage.getItem('cart') || '[]');
            if (localCart.length > 0) {
              await syncCart(currentUser.id, localCart);
            }
          }
        } catch (err) { console.error('Failed to load backend cart:', err); }
      }
    };
    loadBackendCart();
  }, [currentUser]);
  
  // Fetch live products for search and browsing
  useEffect(() => {
    const fetchAllProducts = async () => {
      try {
        setLoadingProducts(true);
        const response = await getProducts();
        if (response.status === 'ok') {
          setAllProducts(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch live products:', err);
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchAllProducts();
  }, []);



  const addToCart = async (item) => {
    setCartItems(prev => [...prev, item]);
    showModal({ title: 'Success', message: `${item.name} added to cart!`, type: 'alert' });
  };

  const updateCart = (newCart) => setCartItems(newCart);

  const showModal = useCallback((config) => {
    setModalInput('');
    setShowPassword(false);
    setModal({ 
      show: true, 
      title: config.title || 'Notification', 
      message: config.message || '', 
      onConfirm: config.onConfirm || null, 
      type: config.type || 'confirm' 
    });
  }, []);

  useEffect(() => {
    // Clean up any stale logout flags left by older code paths
    localStorage.removeItem('logoutSuccess');
    localStorage.removeItem('adminLogoutSuccess');
  }, []);

  const closeModal = useCallback(() => { 
    setModal(prev => ({ ...prev, show: false })); 
    setShowPassword(false); 
  }, []);

  const confirmModal = () => { 
    // Capture onConfirm before closing
    const onConfirmAction = modal.onConfirm;
    const currentInput = modalInput;
    
    // Close modal first
    closeModal();
    
    // Then execute the action
    if (onConfirmAction) {
      onConfirmAction(currentInput);
    }
  };


  const handleLogout = () => {
    showModal({
      title: 'Confirm Logout', message: 'Are you sure you want to log out?', type: 'confirm',
      onConfirm: async () => {
        // Clear all session data first
        localStorage.removeItem('user');
        localStorage.removeItem('cart');
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        setCartItems([]);
        await logout();
        // Use a hard redirect instead of navigate() to avoid a race condition:
        // navigate('/login') fires while React's AuthContext state (currentUser) is
        // still non-null, causing PublicOnlyRoute to bounce the user back to /home.
        // A full page reload guarantees a clean slate.
        window.location.href = '/login';
      }
    });
  };

  const handleSearch = (query) => { 
    setSearchQuery(query); 
    navigate(`/search?q=${encodeURIComponent(query)}`); 
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* NAVBAR */}
      {activePage !== 'buy' && activePage !== 'cart' && (
        <NavBar 
          activePage={activePage} 
          isLoggedIn={!!currentUser} 
          setIsLoggedIn={handleLogout} 
          onSearch={handleSearch} 
          products={allProducts} 
        />
      )}

      <div style={{ flex: 1 }}>
        <Outlet context={{
          currentUser,
          cartItems,
          updateCart,
          checkoutItems,
          setCheckoutItems,
          showModal,
          addToCart,
          allProducts,
          handleSearch,
          searchQuery
        }} />
      </div>

      <Footer />

      {/* MODAL */}
      {modal.show && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: '#ffffff', padding: '30px', borderRadius: '12px', maxWidth: '400px', width: '90%', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '15px' }}>{modal.title}</h3>
            <p style={{ color: '#6b7280', marginBottom: '25px' }}>{modal.message}</p>
            {modal.type === 'password-input' && (
              <div style={{ position: 'relative', width: '100%', marginBottom: '20px' }}>
                <input 
                  type={showPassword ? "text" : "password"} 
                  value={modalInput} 
                  onChange={(e) => setModalInput(e.target.value)} 
                  placeholder="Enter password"
                  style={{ width: '100%', padding: '10px', paddingRight: '40px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' }} 
                  autoFocus 
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            )}

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              {(modal.type === 'confirm' || modal.type === 'password-input') && (
                <button onClick={closeModal} style={{ padding: '10px 25px', backgroundColor: '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Cancel</button>
              )}
              <button onClick={confirmModal} style={{ padding: '10px 25px', backgroundColor: '#2E4B4C', color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                {(modal.type === 'confirm' || modal.type === 'password-input') ? 'Confirm' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;